import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Modal } from '@safetyhub/ui';
import { adminApi, FeatureFlag } from '../../lib/api';
import { useToastStore } from '../../stores/toast';
import OverrideList from './OverrideList';
import FlagAuditLog from './FlagAuditLog';

interface FlagDetailModalProps {
  flagId: string;
  onClose: () => void;
}

type TabType = 'general' | 'overrides' | 'audit';

export default function FlagDetailModal({ flagId, onClose }: FlagDetailModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('general');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'feature-flags', flagId],
    queryFn: () => adminApi.getFeatureFlag(flagId),
  });

  // 세 상태(로딩·실패·본문) 모두 부모가 열 때만 마운트하므로 open은 늘 true다.
  if (isLoading) {
    return (
      <Modal open onClose={onClose} ariaLabel="피처 플래그 상세" size="xl">
        <div className="flex items-center justify-center h-32 text-gray-500">
          로딩 중...
        </div>
      </Modal>
    );
  }

  if (error || !data?.success || !data.data) {
    return (
      <Modal
        open
        onClose={onClose}
        ariaLabel="피처 플래그 상세"
        size="xl"
        footer={
          <Button variant="secondary" onClick={onClose}>
            닫기
          </Button>
        }
      >
        <div className="bg-red-50 p-4 rounded text-red-600 text-sm">
          데이터를 불러오는데 실패했습니다.
        </div>
      </Modal>
    );
  }

  const flag = data.data;

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          {flag.name}
          <StatusBadge status={flag.status} />
        </span>
      }
      ariaLabel={flag.name}
      size="xl"
    >
        <p className="text-sm text-gray-500 font-mono mb-4">{flag.key}</p>

        {/* 탭 */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="-mb-px flex gap-6">
            {(['general', 'overrides', 'audit'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab === 'general' ? '일반 정보' : tab === 'overrides' ? '오버라이드' : '변경 이력'}
              </button>
            ))}
          </nav>
        </div>

        {/* 탭 콘텐츠 */}
        <div className="max-h-[60vh] overflow-y-auto">
          {activeTab === 'general' && <GeneralTab flag={flag} />}
          {activeTab === 'overrides' && (
            <OverrideList flagId={flag.id} overrides={flag.overrides} groupOverrides={flag.groupOverrides} />
          )}
          {activeTab === 'audit' && <FlagAuditLog flagId={flag.id} />}
        </div>
    </Modal>
  );
}

function GeneralTab({ flag }: { flag: FeatureFlag }) {
  const queryClient = useQueryClient();
  const showToast = useToastStore((s) => s.showToast);
  const [name, setName] = useState(flag.name);
  const [description, setDescription] = useState(flag.description || '');
  const [status, setStatus] = useState(flag.status);
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>(flag.tags);

  const updateMutation = useMutation({
    mutationFn: () =>
      adminApi.updateFeatureFlag(flag.id, {
        name,
        description: description || undefined,
        status,
        tags,
      }),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags', flag.id] });
        showToast('피처 플래그가 업데이트되었습니다.', 'success');
      } else {
        showToast(result.error?.message || '업데이트에 실패했습니다.', 'error');
      }
    },
    onError: () => {
      showToast('업데이트 중 오류가 발생했습니다.', 'error');
    },
  });

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
      setTagInput('');
    }
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      showToast('이름은 필수입니다.', 'error');
      return;
    }
    updateMutation.mutate();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Key</label>
          <input
            type="text"
            value={flag.key}
            disabled
            className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50 text-gray-500 font-mono text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
          <input
            type="text"
            value={flag.type}
            disabled
            className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50 text-gray-500 text-sm"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as 'ACTIVE' | 'ARCHIVED')}
          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
        >
          <option value="ACTIVE">Active</option>
          <option value="ARCHIVED">Archived</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleTagKeyDown}
            placeholder="태그 입력 (Enter)"
            className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
          />
          <button
            type="button"
            onClick={handleAddTag}
            disabled={!tagInput.trim()}
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 text-sm"
          >
            추가
          </button>
        </div>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => setTags(tags.filter((t) => t !== tag))}
                  className="text-gray-400 hover:text-gray-600"
                >
                  x
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* 메타 정보 */}
      <div className="pt-4 border-t text-sm text-gray-500 flex gap-4">
        <span>생성일: {new Date(flag.createdAt).toLocaleDateString('ko-KR', { timeZone: 'Asia/Seoul' })}</span>
        <span>수정일: {new Date(flag.updatedAt).toLocaleDateString('ko-KR', { timeZone: 'Asia/Seoul' })}</span>
      </div>

      <div className="flex justify-end pt-2">
        <Button type="submit" variant="primary" disabled={updateMutation.isPending}>
          {updateMutation.isPending ? '저장 중...' : '저장'}
        </Button>
      </div>
    </form>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-600',
    ACTIVE: 'bg-green-100 text-green-700',
    ARCHIVED: 'bg-yellow-100 text-yellow-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}
